/** Search the catalog without filtering the artist directory out of view. */
class GallerySearch extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.input = this.querySelector('input');
    this.results = this.querySelector('.gallery-search__results');
    this.status = this.querySelector('[role="status"]');
    this.discover = this.querySelector('.gallery-search__discover');
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.request?.abort();
      this.results.hidden = true;
      this.results.replaceChildren();
      this.status.textContent = '';
      if (this.discover) this.discover.hidden = Boolean(this.input.value.trim());
      if (this.input.value.trim()) this.timer = setTimeout(() => this.search(), 220);
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.results.hidden = true;
    }, { signal });
    this.input.addEventListener('focus', () => {
      if (this.results.childElementCount && this.input.value.trim()) this.results.hidden = false;
    }, { signal });
    this.querySelector('form').addEventListener('submit', () => {
      this.input.value = this.query();
      this.querySelector('[name="options[fields]"]').value = 'title,vendor';
      this.querySelector('[name="options[unavailable_products]"]').value = 'show';
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.request?.abort();
        clearTimeout(this.timer);
        this.input.focus();
        this.results.hidden = true;
      }
      if (event.key === 'ArrowDown' && event.target === this.input && !this.results.hidden) {
        const link = this.results.querySelector('a');
        if (link) { event.preventDefault(); link.focus(); }
      }
    }, { signal });
  }

  query() {
    // A copied artwork label often ends in a year absent from its Shopify title.
    return this.input.value.trim().replace(/\*|[“”]/g, '').replace(/,?\s+(?:19|20)\d{2}\s*$/, '').trim();
  }

  async search() {
    const query = this.query();
    if (!query) return;
    const request = new AbortController();
    this.request = request;
    this.status.textContent = 'Searching…';
    const url = new URL(this.dataset.suggestUrl, location.origin);
    url.search = new URLSearchParams({q: query, section_id: 'gallery-search-results', 'resources[type]': 'product,collection,article', 'resources[limit]': '10', 'resources[limit_scope]': 'each', 'resources[options][fields]': 'title,vendor', 'resources[options][unavailable_products]': 'show'});
    try {
      const response = await fetch(url, {signal: request.signal});
      if (!response.ok) throw new Error('Search unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-gallery-results]');
      if (!content) throw new Error('Missing results');
      if (request.signal.aborted) return;
      // Put direct matches first without removing Shopify's broader suggestions.
      const words = (text) => text.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
      const terms = words(query);
      const score = (item) => {
        const title = words(item.querySelector('strong')?.textContent || item.textContent);
        if (title.join(' ') === terms.join(' ')) return 2;
        const candidate = words(item.textContent);
        return Number(terms.every((term) => candidate.some((word) => word.startsWith(term))));
      };
      for (const list of content.querySelectorAll('ul')) {
        list.append(...Array.from(list.children).sort((a, b) => score(b) - score(a)));
      }
      this.results.replaceChildren(content);
      this.results.hidden = false;
      this.status.textContent = content.dataset.summary;
    } catch (error) {
      if (error.name !== 'AbortError') this.status.textContent = 'Suggestions are unavailable. Press Search to see results.';
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.controller.abort();
  }
}
if (!customElements.get('gallery-search')) customElements.define('gallery-search', GallerySearch);
